import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent } from '../components/ui/card';
import { trackEvent } from '../components/GoogleAnalytics';
import { toast } from 'sonner';

const BOOKING_URL = "https://outlook.office.com/book/SAADINFORMATIQUE1@saad-informatique.fr/?ismsaljsauthenabled";
// URL relative en production : même origine HTTPS, évite le contenu mixte (Edge/Chrome).
const API_URL =
  process.env.NODE_ENV === 'production'
    ? '/api'
    : `${process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8001'}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    societe: '',
    typeBesoin: '',
    message: '',
    rgpdConsent: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, typeBesoin: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.rgpdConsent) {
      toast.error('Veuillez accepter la politique de confidentialité');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API_URL}/contact`, {
        nom: formData.nom,
        email: formData.email,
        telephone: formData.telephone || null,
        societe: formData.societe || null,
        type_besoin: formData.typeBesoin || null,
        message: formData.message,
        rgpd_consent: formData.rgpdConsent
      });
      
      if (response.data.success) {
        toast.success('Votre message a été envoyé ! Je vous recontacterai rapidement.');
        trackEvent('contact_form_submit', {
          event_category: 'Contact',
          event_label: formData.typeBesoin || 'non_precise'
        });
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          societe: '',
          typeBesoin: '',
          message: '',
          rgpdConsent: false
        });
      }
    } catch (error) {
      console.error('Erreur envoi formulaire:', error);
      const errorMessage = error.response?.data?.detail || "Une erreur s'est produite. Veuillez réessayer ou m'envoyer un email directement.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 relative overflow-hidden">
      {/* Décors d'ambiance subtils */}
      <div className="absolute top-40 -left-20 w-72 h-72 bg-gradient-to-br from-blue-200/20 to-cyan-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-[700px] -right-20 w-80 h-80 bg-gradient-to-br from-orange-200/20 to-pink-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-40 left-1/3 w-96 h-96 bg-gradient-to-br from-purple-200/15 to-blue-200/15 rounded-full blur-3xl pointer-events-none" />

      {/* Hero */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-cyan-50 relative">
        <div className="mx-auto max-w-4xl text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Contactez-moi
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed">
            Vous avez un projet, une difficulté récurrente ou simplement besoin d'y voir plus clair sur votre informatique ? 
            Parlons-en lors d'une visio de 30 minutes ou décrivez-moi votre besoin via le formulaire ci-dessous.
          </p>
        </div>
      </section>

      {/* Contact Options */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Prise de RDV */}
            <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Phone className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
                    Prendre rendez-vous
                  </h2>
                  <p className="text-gray-600 mb-6 text-sm sm:text-base">
                    Réservez un créneau de 30 minutes pour un échange en visio, sans engagement.
                  </p>
                  <Button
                    asChild
                    size="lg"
                    className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white w-full shadow-lg"
                  >
                    <a 
                      href={BOOKING_URL} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={() => trackEvent('booking_click', { event_category: 'Conversion', event_label: 'Contact page CTA' })}
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      Réserver un créneau
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Coordonnées */}
            <Card className="border-gray-200 hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Mail className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
                    Mes coordonnées
                  </h2>
                  <div className="space-y-4 text-left">
                    <div className="flex items-start space-x-3">
                      <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-600">Email</p>
                        <a href="mailto:richard@saad-informatique.fr" className="text-gray-900 font-medium hover:text-blue-600 break-all text-sm sm:text-base">
                          richard@saad-informatique.fr
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <MapPin className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-600">Zone d'intervention</p>
                        <p className="text-gray-900 font-medium">Lyon et sa région</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Formulaire */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="mx-auto max-w-3xl">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Ou envoyez-moi un message
            </h2>
            <p className="text-gray-600 text-sm sm:text-base">
              Remplissez le formulaire ci-dessous, je vous recontacterai dans les plus brefs délais.
            </p>
          </div>

          <Card className="shadow-xl">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6" data-testid="contact-form">
                {/* Nom */}
                <div>
                  <Label htmlFor="nom">Nom *</Label>
                  <Input
                    id="nom"
                    name="nom"
                    type="text"
                    required
                    value={formData.nom}
                    onChange={handleChange}
                    placeholder="Votre nom complet"
                    className="mt-1"
                    data-testid="contact-name-input"
                  />
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="votre@email.fr"
                    className="mt-1"
                    data-testid="contact-email-input"
                  />
                </div>

                {/* Téléphone (optionnel) */}
                <div>
                  <Label htmlFor="telephone">Téléphone (optionnel)</Label>
                  <Input
                    id="telephone"
                    name="telephone"
                    type="tel"
                    value={formData.telephone}
                    onChange={handleChange}
                    placeholder="06 12 34 56 78"
                    className="mt-1"
                    data-testid="contact-phone-input"
                  />
                </div>

                {/* Société */}
                <div>
                  <Label htmlFor="societe">Société (optionnel)</Label>
                  <Input
                    id="societe"
                    name="societe"
                    type="text"
                    value={formData.societe}
                    onChange={handleChange}
                    placeholder="Nom de votre entreprise"
                    className="mt-1"
                    data-testid="contact-company-input"
                  />
                </div>

                {/* Type de besoin */}
                <div>
                  <Label htmlFor="typeBesoin">Type de besoin (optionnel)</Label>
                  <Select value={formData.typeBesoin} onValueChange={handleSelectChange}>
                    <SelectTrigger className="mt-1" data-testid="contact-type-select">
                      <SelectValue placeholder="Sélectionnez un type de besoin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="infogerance">Infogérance & maintenance</SelectItem>
                      <SelectItem value="depannage">Dépannage ponctuel</SelectItem>
                      <SelectItem value="infrastructure">Projet d'infrastructure</SelectItem>
                      <SelectItem value="m365">Cloud & Microsoft 365</SelectItem>
                      <SelectItem value="securite">Sécurité & sauvegarde</SelectItem>
                      <SelectItem value="conseil">Conseil / Audit</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Décrivez brièvement votre besoin ou votre situation..."
                    rows={6}
                    className="mt-1"
                    data-testid="contact-message-input"
                  />
                </div>

                {/* RGPD Consent */}
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="rgpdConsent"
                    name="rgpdConsent"
                    checked={formData.rgpdConsent}
                    onCheckedChange={(checked) => 
                      setFormData(prev => ({ ...prev, rgpdConsent: checked }))
                    }
                    data-testid="contact-rgpd-checkbox"
                  />
                  <Label htmlFor="rgpdConsent" className="text-sm text-gray-600 font-normal cursor-pointer">
                    En soumettant ce formulaire, vous acceptez que vos informations soient utilisées 
                    uniquement pour vous recontacter dans le cadre de votre demande. *
                  </Label>
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-lg disabled:opacity-50"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? (
                    'Envoi en cours...'
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Envoyer ma demande
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};
